import Router from 'vue-router';
import domainModeHandler from '@/common/routes/handlers/domainModeHandler';
import permissionRedirectHandler from '@/common/routes/handlers/permissionRedirectHandler';
import afterPermissionRedirectHandler from '@/common/routes/handlers/afterPermissionRedirectHandler';
import loadLanguagesHandler from '@/common/routes/handlers/loadLanguagesHandler';
import telemetryRouteHandler from '@/common/routes/handlers/telemetryRouteHandler';
import useAppModeStore from '@/common/store/pinia/appMode';
import swallowNavigationErrorPatch from '@/common/routes/patches/swallowNavigationErrorPatch';
import isPreloadError from '@/common/utils/preloadError.util';

// Bounds preload-failure recovery to a single attempt per tab. Held in sessionStorage rather
// than a module-scoped boolean because the recovery navigation rebuilds the JS context, and
// keyed per app: the admin and self-service panels share an origin, so an unscoped key would
// let one panel's armed guard suppress recovery in the other.
const recoveryGuardKeys = (baseURL) => ({
  attempted: `preloadErrorRecoveryAttempted:${baseURL}`,
  path: `preloadErrorRecoveryPath:${baseURL}`
});

export default function ({ routes, baseURL, pinia }) {
  const appModeStore = useAppModeStore(pinia);

  swallowNavigationErrorPatch();

  const router = new Router({
    mode: appModeStore.preset.router.mode,
    base: baseURL,
    linkActiveClass: 'active',
    saveScrollPosition: true,
    parseQuery(query) {
      return JSON.parse(decodeURI(query) || '{}');
    },
    stringifyQuery(query) {
      const result = JSON.stringify(query);

      return result === '{}' ? '' : `?${result}`;
    },
    routes
  });

  const { attempted: guardKey, path: guardPathKey } = recoveryGuardKeys(baseURL);
  let pendingNavigation = null;

  router.beforeEach((to, from, next) => {
    pendingNavigation = to;
    next();
  });
  router.beforeEach(domainModeHandler);
  if (appModeStore.shouldUsePermissionRedirect) {
    router.beforeEach(permissionRedirectHandler);
  }
  router.beforeEach(afterPermissionRedirectHandler);
  router.beforeEach(loadLanguagesHandler(pinia));
  router.afterEach(telemetryRouteHandler);
  router.afterEach((to) => {
    if (!window.sessionStorage.getItem(guardKey)) {
      return;
    }
    // Compared on `path`, not `fullPath`: the guard is about reaching the route, and this
    // project's `stringifyQuery` serializes the query as JSON, whose key order need not survive
    // a round trip.
    const recoveryPath = window.sessionStorage.getItem(guardPathKey);
    if (recoveryPath && recoveryPath !== to.path) {
      return;
    }
    window.sessionStorage.removeItem(guardKey);
    window.sessionStorage.removeItem(guardPathKey);
  });

  router.onError((navigationError) => {
    if (!isPreloadError(navigationError)) {
      return;
    }

    if (appModeStore.preset.router.mode === 'abstract') {
      // Embedded (federated) mode shares the host's document: navigating away would discard the
      // host's state, and the abstract history the host restores would not bring the failed
      // route back. The error is left to reject so it still reaches the console and telemetry.
      return;
    }

    if (window.sessionStorage.getItem(guardKey)) {
      // A recovery attempt already ran in this tab: let the error surface instead of attempting
      // again, which would loop forever against an asset that is permanently missing (e.g.
      // deleted by an RPM upgrade).
      return;
    }
    window.sessionStorage.setItem(guardKey, 'true');
    if (pendingNavigation) {
      window.sessionStorage.setItem(guardPathKey, pendingNavigation.path);
    }

    // Navigate to the failed route rather than reloading in place. A same-page retry cannot
    // recover the asset (Vite's __vitePreload skips any dep whose <link> is already in the
    // document, and the failed <link> is never removed), but `location.reload()` is wrong too:
    // vue-router only pushes the URL once the async component resolves, so the document would be
    // rebuilt on the PREVIOUS route - costing the user that page without ever reaching the
    // target. Assigning the target's href refetches index.html and lands on the route.
    let recoveryHref = null;
    if (pendingNavigation) {
      try {
        recoveryHref = router.resolve(pendingNavigation.fullPath).href;
      } catch (resolveError) {
        recoveryHref = null;
      }
    }

    if (recoveryHref) {
      window.location.assign(recoveryHref);
      return;
    }
    window.location.reload();
  });

  if (appModeStore.preset.router.mode === 'abstract') {
    router.history.transitionTo(router.history.getCurrentLocation());
  }

  return router;
}
